import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import logo from './assets/logo.png';

const ProfileModal = ({ profile, onClose }) => {
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [modalIsMobile] = useState(window.innerWidth <= 768);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!profile?.images || profile.images.length <= 1) return;
    const interval = setInterval(() => {
      const scrollTop = overlayRef.current?.scrollTop || 0;
      setModalImageIndex(prev => (prev + 1) % profile.images.length);
      requestAnimationFrame(() => {
        if (overlayRef.current) overlayRef.current.scrollTop = scrollTop;
      });
    }, 3800);
    return () => clearInterval(interval);
  }, [profile?.images]);

  useEffect(() => {
    const handleEsc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        alignItems: modalIsMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 9999,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        backdropFilter: 'blur(10px)',
        padding: modalIsMobile ? '0' : '2rem',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(165deg, #1E2530 0%, #0F1419 100%)',
          borderRadius: modalIsMobile ? '0' : '24px',
          width: '100%',
          maxWidth: modalIsMobile ? '100%' : '1100px',
          position: 'relative',
          boxShadow: '0 40px 90px rgba(0,0,0,0.7)',
          minHeight: modalIsMobile ? '100dvh' : 'auto',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'fixed',
            top: modalIsMobile ? '0.75rem' : '1.25rem',
            right: modalIsMobile ? '0.75rem' : '1.25rem',
            width: modalIsMobile ? '40px' : '48px',
            height: modalIsMobile ? '40px' : '48px',
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.85)',
            border: '1px solid rgba(199,179,129,0.5)',
            color: '#C7B381',
            fontSize: modalIsMobile ? '1.5rem' : '2rem',
            fontWeight: '300',
            cursor: 'pointer',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(199,179,129,0.2)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.85)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ×
        </button>

        <div style={{
          display: 'flex',
          flexDirection: modalIsMobile ? 'column' : 'row',
          minHeight: modalIsMobile ? '100dvh' : 'auto',
        }}>
          {/* Image panel */}
          <div style={{
            width: modalIsMobile ? '100%' : '42%',
            height: modalIsMobile ? '320px' : '600px',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #0a0e14 0%, #1a1f2e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: modalIsMobile ? '0' : '24px 0 0 24px',
            flexShrink: 0,
          }}>
            {profile.images ? (
              profile.images.map((img, idx) => (
                <img
                  key={img}
                  src={img}
                  alt=""
                  loading="eager"
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    opacity: idx === modalImageIndex ? 1 : 0,
                    transition: 'opacity 1.3s ease-in-out',
                    pointerEvents: 'none'
                  }}
                />
              ))
            ) : (
              <img
                src={profile.image}
                alt={profile.name}
                loading="eager"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}

            {profile.images && profile.images.length > 1 && (
              <div style={{
                position: 'absolute',
                bottom: '1rem',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: modalIsMobile ? '8px' : '10px',
                background: 'rgba(0,0,0,0.75)',
                padding: modalIsMobile ? '6px 12px' : '8px 16px',
                borderRadius: '999px',
                backdropFilter: 'blur(8px)'
              }}>
                {profile.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={e => { e.stopPropagation(); setModalImageIndex(idx); }}
                    style={{
                      width: idx === modalImageIndex ? (modalIsMobile ? '24px' : '28px') : (modalIsMobile ? '8px' : '10px'),
                      height: modalIsMobile ? '8px' : '10px',
                      borderRadius: '999px',
                      background: idx === modalImageIndex ? '#C7B381' : 'rgba(199,179,129,0.4)',
                      border: 'none',
                      transition: 'all 0.35s ease',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Text panel */}
          <div style={{
            width: modalIsMobile ? '100%' : '58%',
            padding: modalIsMobile ? '1.5rem 1.25rem 3rem' : '2.5rem 3rem',
            color: '#e0e0e0',
            boxSizing: 'border-box',
            paddingTop: modalIsMobile ? '1.5rem' : '2.5rem',
          }}>
            <div style={{
              color: '#C7B381',
              fontSize: modalIsMobile ? '0.8rem' : '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: '1rem'
            }}>
              {profile.title}
            </div>
            <h2 style={{
              fontSize: modalIsMobile ? '1.6rem' : '2.5rem',
              color: 'white',
              marginBottom: '1.5rem',
              fontWeight: 400,
              lineHeight: 1.2
            }}>
              {profile.name}
            </h2>
            <p style={{
              fontSize: modalIsMobile ? '0.95rem' : '1.05rem',
              lineHeight: 1.8,
              color: '#d0d0d0',
              fontWeight: 300,
              margin: 0,
            }}>
              {profile.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CircularAvatar = ({ person, size, onClick, cardKey, showBadge = false, isMobile, hoveredCard, setHoveredCard }) => {
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    if (!person.images || person.images.length <= 1) return;
    const interval = setInterval(() => {
      setImageIndex(prev => (prev + 1) % person.images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [person.images]);

  const isHovered = hoveredCard === cardKey;

  return (
    <div
      onClick={() => onClick(person)}
      onMouseEnter={() => setHoveredCard(cardKey)}
      onMouseLeave={() => setHoveredCard(null)}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <div style={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        border: showBadge ? '4px solid #C7B381' : `3px solid ${isHovered ? '#C7B381' : 'rgba(199,179,129,0.4)'}`,
        boxShadow: isHovered
          ? '0 20px 50px rgba(199,179,129,0.4), 0 0 0 6px rgba(199,179,129,0.15)'
          : '0 10px 30px rgba(0,0,0,0.5)',
        transition: 'all 0.4s ease',
        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
        position: 'relative',
        background: 'linear-gradient(135deg, #0a0e14 0%, #1a1f2e 100%)'
      }}>
        {person.images ? (
          person.images.map((img, idx) => (
            <img
              key={img}
              src={img}
              alt=""
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: idx === imageIndex ? 1 : 0,
                transition: 'opacity 1.2s ease-in-out',
                pointerEvents: 'none'
              }}
            />
          ))
        ) : (
          <img
            src={person.image}
            alt={person.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {showBadge && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: '#C7B381',
            color: '#0F1419',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
          }}>★</div>
        )}
      </div>

      <div style={{ marginTop: '1rem', textAlign: 'center', maxWidth: size, transition: 'all 0.3s ease' }}>
        <div style={{
          fontSize: isMobile ? '0.95rem' : '1.1rem',
          fontWeight: 600,
          color: isHovered ? '#C7B381' : 'white',
          marginBottom: '0.3rem',
          transition: 'color 0.3s ease'
        }}>
          {person.name}
        </div>
        <div style={{
          fontSize: isMobile ? '0.7rem' : '0.8rem',
          color: '#C7B381',
          fontWeight: 500,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          opacity: 0.9
        }}>
          {person.title}
        </div>
      </div>

      {person.images && person.images.length > 1 && (
        <div style={{ display: 'flex', gap: '4px', marginTop: '0.5rem' }}>
          {person.images.map((_, idx) => (
            <div key={idx} style={{
              width: idx === imageIndex ? '16px' : '6px',
              height: '6px',
              borderRadius: '3px',
              background: idx === imageIndex ? '#C7B381' : 'rgba(199,179,129,0.3)',
              transition: 'all 0.3s ease'
            }} />
          ))}
        </div>
      )}
    </div>
  );
};

// Connector line between org tree nodes
const ConnectorLine = ({ isMobile }) => (
  <div style={{
    width: '2px',
    height: isMobile ? '40px' : '60px',
    background: 'linear-gradient(180deg, rgba(199,179,129,0.6), rgba(199,179,129,0.3))',
    marginTop: '-1rem',
    marginBottom: '-1rem'
  }} />
);

// Horizontal branch SVG for desktop tree
const BranchConnector = ({ count }) => {
  const positions2 = ['30%', '70%'];
  const positions3 = ['20%', '50%', '80%'];
  const positions = count === 2 ? positions2 : positions3;
  return (
    <svg style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', height: '100px', pointerEvents: 'none', zIndex: 0 }}>
      <defs>
        <linearGradient id="branchGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: 'rgba(199,179,129,0.3)', stopOpacity: 1 }} />
          <stop offset="50%" style={{ stopColor: 'rgba(199,179,129,0.6)', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: 'rgba(199,179,129,0.3)', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <line x1={positions[0]} y1="0" x2={positions[positions.length - 1]} y2="0" stroke="url(#branchGradient)" strokeWidth="2" />
      {positions.map((pos, idx) => (
        <line key={idx} x1={pos} y1="0" x2={pos} y2="40" stroke="rgba(199,179,129,0.5)" strokeWidth="2" />
      ))}
    </svg>
  );
};

function CompanyBackground() {
  const [, setCurrentFounderIndex] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [serviceCenterIndex, setServiceCenterIndex] = useState(0);
  const [gacIndex, setGacIndex] = useState(0);
  const [ridaraIndex, setRidaraIndex] = useState(0);
  const [dbImages, setDbImages] = useState({
    founder: [],
    executiveDirector: null,
    // director category => Pema Lhamo (Finance Director)
    director: null,
    // generalManagerHead category => Ugyen Phuntsho Rabgay (Director of Operations)
    generalManagerHead: null,
    generalManagers: [],
    serviceCenter: [],
    gac: [],
    ridara: [],
    customer: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/api/images/all`, {
          headers: {
            'Accept': 'application/json',
            'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
          },
        });
        const allImages = response.data.images || [];
        const organized = {
          founder: allImages.filter(img => img.category === 'founder' && img.isActive).sort((a, b) => a.order - b.order),
          executiveDirector: allImages.find(img => img.category === 'executiveDirector' && img.isActive),
          // Pema Lhamo — stored as category: "director"
          director: allImages.find(img => img.category === 'Finance' && img.isActive && img.order === 0),


          // Ugyen Phuntsho Rabgay — stored as category: "generalManagerHead"
          generalManagerHead: allImages.find(img => img.category === 'generalManagerHead' && img.isActive),
          // Sonam Dendup & Kinga Tandin — stored as category: "generalManager"
          generalManagers: allImages.filter(img => img.category === 'generalManager' && img.isActive).sort((a, b) => a.order - b.order),
          serviceCenter: allImages.filter(img => img.category === 'serviceCenter' && img.isActive).sort((a, b) => a.order - b.order),
          gac: allImages.filter(img => img.category === 'gac' && img.isActive).sort((a, b) => a.order - b.order),
          ridara: allImages.filter(img => img.category === 'ridara' && img.isActive).sort((a, b) => a.order - b.order),
          customer: allImages.filter(img => img.category === 'customer' && img.isActive).sort((a, b) => a.order - b.order)
        };
        setDbImages(organized);
      } catch (err) {
        console.error('Failed to load images:', err);
        setError(err.response?.data?.message || 'Failed to load company images');
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  useEffect(() => {
    const handleContextMenu = e => e.preventDefault();
    const handleKeyDown = e => {
      if (e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'U') ||
        (e.metaKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.metaKey && e.key === 'U')) {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (dbImages.founder.length > 1) {
      const timer = setInterval(() => {
        setCurrentFounderIndex(prev => (prev + 1) % dbImages.founder.length);
      }, 3000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbImages.founder.length]);

  useEffect(() => {
    const timers = [];
    if (dbImages.serviceCenter.length > 1) {
      timers.push(setInterval(() => setServiceCenterIndex(i => (i + 1) % dbImages.serviceCenter.length), 5000));
    }
    if (dbImages.gac.length > 1) {
      timers.push(setInterval(() => setGacIndex(i => (i + 1) % dbImages.gac.length), 5000));
    }
    if (dbImages.ridara.length > 1) {
      timers.push(setInterval(() => setRidaraIndex(i => (i + 1) % dbImages.ridara.length), 5000));
    }
    return () => timers.forEach(clearInterval);
  }, [dbImages.serviceCenter.length, dbImages.gac.length, dbImages.ridara.length]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openProfile = useCallback((profile) => {
    if (!profile) return;
    document.body.style.overflow = 'hidden';
    setSelectedProfile(profile);
  }, []);

  const closeProfile = useCallback(() => {
    document.body.style.overflow = '';
    setSelectedProfile(null);
  }, []);

  const companyData = useMemo(() => ({
    // ── Tier 1: Founder ──────────────────────────────────────────────────
    founder: {
      name: dbImages.founder[0]?.contactInfo?.name || "Mr. Sangay Tsheltrim",
      title: "Chairman & Founder",
      images: dbImages.founder.map(img => img.imageUrl),
      description: dbImages.founder[0]?.alt ||
        "ST Motors was founded by Mr. Sangay Tsheltrim, a respected entrepreneur and former Royal Bodyguards officer who served His Majesty the King. With over a decade of experience in the automotive industry, he is recognized for his commitment to excellence, innovation, and national service. He has also represented Bhutan internationally as both an actor and athlete, bringing pride and recognition to the country."
    },

    // ── Tier 2: Executive Director ───────────────────────────────────────
    executiveDirector: dbImages.executiveDirector ? {
      name: dbImages.executiveDirector.contactInfo?.name || "Executive Director",
      title: dbImages.executiveDirector.contactInfo?.position || "Executive Director",
      image: dbImages.executiveDirector.imageUrl,
      description: dbImages.executiveDirector.alt ||
        "He began his career in 1989 with the State Trading Corporation of Bhutan Limited (STCBL), serving until 2012 and rising to the position of General Manager. He later served as a Member of Parliament representing DNT. He holds a Diploma in Mechanical Engineering from the Royal Bhutan Polytechnic, Deothang, a Bachelor's degree from SSM College of Arts and Science (Periyar University, India), and a Master's degree from Kaplan International Business School, Melbourne, Australia. With strong leadership and extensive professional experience, he currently serves as the Executive Director of Sangay Tsheltrim Pvt Limited."
    } : null,

    // ── Tier 3a: Director of Operations — Ugyen Phuntsho Rabgay ─────────
    // Fetched from category: "generalManagerHead"
    rabgay: dbImages.generalManagerHead ? {
      name: dbImages.generalManagerHead.contactInfo?.name || "Ugyen Phuntsho Rabgay",
      title: dbImages.generalManagerHead.contactInfo?.position || "Director of Operations",
      image: dbImages.generalManagerHead.imageUrl,
      description: dbImages.generalManagerHead.alt ||
        "Ugyen Phuntsho Rabgay serves as Director of Operations at ST Motors. He plays a key role in driving the company's operational strategy and promoting electric vehicle adoption. Holding a BA in Journalism & Mass Communication with a specialization in Digital Filmmaking, he brings strong expertise in storytelling and visual communication. In addition to his corporate leadership, he is actively involved in the Bhutanese entertainment industry as an actor, director, producer, and show host."
    } : null,

    pemaLhamo: dbImages.director ? {
  name: dbImages.director.contactInfo?.name || "Pema Lhamo",
  title: dbImages.director.contactInfo?.position || "Director of Finance",
  image: dbImages.director.imageUrl,
  description: dbImages.director.alt ||
    "Pema Lhamo currently serves as the Finance Director at Sangay Tsheltrim Pvt Ltd. She possesses more than 10 years of professional experience working in Government agencies in Bhutan, with extensive expertise in finance, administration, and organizational management. She holds a Degree in Finance along with a Post Graduate Degree in Finance and Management from the Royal Institute of Management, Simtokha. At Sangay Tsheltrum Pvt Ltd, her key roles and responsibilities include overseeing financial operations, managing budgets and expenditures, ensuring compliance with financial policies and procedures, and monitoring financial transactions. She ensures effective financial management and accountability within the organization. In addition to her corporate and government experience, Pema Lhamo has also contributed to Bhutan's film industry by working on few film projects as a Line Producer and Script Supervisor. Her involvement in film production includes coordinating production logistics, managing schedules and resources, supervising script continuity, and supporting overall project execution to ensure smooth production operations."
} : null,
    // ── Tier 4: General Managers — Sonam Dendup & Kinga Tandin ──────────
    // Fetched from category: "generalManager", sorted by order
    // order 0 → Sonam Dendup (Service & Spares)
    // order 1 → Kinga Tandin (HR & Administration)
    generalManagers: dbImages.generalManagers.map((gm, idx) => {
      const defaultNames = ["Sonam Dendup", "Kinga Tandin"];
      const defaultTitles = ["General Manager – Service & Spares", "General Manager – HR & Administration"];
      const defaultDescriptions = [
        "As General Manager of Service & Spares, oversees service center operations, spare parts management, and after-sales support. Ensures high standards of customer service, technical excellence, and efficient supply chain management, while leading a skilled team to deliver reliable vehicle maintenance and repair services.",
        "Kinga Tandin serves as General Manager – HR & Administration at ST Motors, providing strong leadership in human resources, administrative governance, compliance, and organizational management. He supports sustainable business growth through effective people management and streamlined administrative operations."
      ];
      return {
        name: gm.contactInfo?.name || defaultNames[idx] || "General Manager",
        title: gm.contactInfo?.position || defaultTitles[idx] || "General Manager",
        image: gm.imageUrl,
        description: gm.alt || defaultDescriptions[idx] || "Key leadership member at ST Motors"
      };
    })
  }), [dbImages]);

  const visionMission = useMemo(() => ({
    vision: "At Sangay Tsheltrim Motors, we envision leading the electric vehicle revolution in Bhutan and beyond by setting new standards for sustainability, innovation, and customer excellence. We strive to be the most trusted automotive partner, contributing to a greener future while empowering our communities through cutting-edge mobility solutions.",
    mission: "At Sangay Tsheltrim Motors, our mission is to lead the transition to sustainable transportation by delivering high-quality electric vehicles that meet the evolving needs of our customers. We are committed to providing exceptional service, fostering innovation, and building lasting relationships based on trust and excellence.",
    values: [
      { title: "Quality Assurance", desc: "Maintaining uncompromising standards" },
      { title: "Customer-Centric", desc: "Placing needs at our core" },
      { title: "Innovation", desc: "Leading with cutting-edge solutions" },
      { title: "Sustainability", desc: "Building a greener tomorrow" },
      { title: "Community", desc: "Growing together" },
      { title: "Excellence", desc: "Comprehensive development" }
    ]
  }), []);

  const facilities = useMemo(() => [
    {
      title: "Service Center",
      description: "State-of-the-art service facility equipped with advanced diagnostic tools and dedicated EV service bays. Our trained technicians ensure top-quality maintenance and repairs.",
      staffImage: dbImages.serviceCenter.find(img => img.alt?.toLowerCase().includes('staff'))?.imageUrl,
      gallery: dbImages.serviceCenter.map(img => img.imageUrl),
      currentIndex: serviceCenterIndex,
      setIndex: setServiceCenterIndex
    },
    {
      title: "GAC + AION Showroom",
      description: "Premium showroom showcasing the full range of GAC Motor vehicles. Modern design, comfortable customer lounge, and professional sales consultants.",
      staffImage: dbImages.gac.find(img => img.alt?.toLowerCase().includes('staff'))?.imageUrl,
      gallery: dbImages.gac.map(img => img.imageUrl),
      currentIndex: gacIndex,
      setIndex: setGacIndex
    },
    {
      title: "RIDARA Showroom",
      description: "Exclusive RIDARA brand showroom featuring the latest models with elegant display layout and dedicated customer experience zones.",
      staffImage: dbImages.ridara.find(img => img.alt?.toLowerCase().includes('staff'))?.imageUrl,
      gallery: dbImages.ridara.map(img => img.imageUrl),
      currentIndex: ridaraIndex,
      setIndex: setRidaraIndex
    }
  ].filter(facility => facility.gallery.length > 0), [
    dbImages.serviceCenter, dbImages.gac, dbImages.ridara,
    serviceCenterIndex, gacIndex, ridaraIndex
  ]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0F1419', color: '#C7B381', fontSize: '1.5rem', fontWeight: '300' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '50px', height: '50px', border: '3px solid rgba(199,179,129,0.2)', borderTop: '3px solid #C7B381', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
          Loading company profile...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0F1419', color: '#ff6b6b', textAlign: 'center', padding: '2rem' }}>
        <h2 style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', marginBottom: '1rem' }}>⚠️ Error</h2>
        <p style={{ fontSize: 'clamp(1rem, 3vw, 1.3rem)', maxWidth: '600px' }}>{error}</p>
      </div>
    );
  }

  // ─── Org tree helpers ─────────────────────────────────────────────────────
  // Tier 3: Rabgay + Pema Lhamo side by side
  const tier3 = [companyData.rabgay, companyData.pemaLhamo].filter(Boolean);
  // Tier 4: Sonam + Kinga Tandin
  const tier4 = companyData.generalManagers;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0F1419',
      color: '#E8E8E8',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      paddingBottom: '3rem'
    }}>
      {selectedProfile && <ProfileModal profile={selectedProfile} onClose={closeProfile} />}

      {/* Hero */}
      <div style={{ padding: isMobile ? '4rem 1.5rem' : 'clamp(5rem, 12vw, 10rem) clamp(2rem, 5vw, 4rem)', textAlign: 'center', background: '#0F1419' }}>
        <img src={logo} alt="ST Motors Logo" style={{ width: isMobile ? '120px' : 'clamp(100px, 18vw, 160px)', marginBottom: isMobile ? '1.5rem' : '2.5rem' }} />
        <h1 style={{ fontSize: isMobile ? '2.5rem' : 'clamp(3rem, 8vw, 6.5rem)', fontWeight: 300, lineHeight: 1.1 }}>
          Company <span style={{ color: '#C7B381', fontWeight: 600 }}>Profile</span>
        </h1>
        <p style={{ fontSize: isMobile ? '1rem' : 'clamp(1.2rem, 2.5vw, 1.6rem)', color: '#B8B8B8', maxWidth: '900px', margin: '1.5rem auto 3rem', padding: isMobile ? '0 1rem' : '0' }}>
          Pioneering sustainable automotive excellence in Bhutan through innovation, integrity, and unwavering commitment to our nation's future
        </p>
      </div>

      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: isMobile ? '0 1rem' : '0 clamp(2rem, 5vw, 4rem)', background: '#0F1419' }}>

        {/* ═══════════════════════════════════════════════════════════════
            ORGANIZATIONAL STRUCTURE
        ════════════════════════════════════════════════════════════════ */}
        <section style={{ marginBottom: isMobile ? '5rem' : 'clamp(7rem, 12vw, 12rem)' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '3rem' : '5rem' }}>
            <h2 style={{ fontSize: isMobile ? '2rem' : 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 300, marginBottom: '1rem' }}>
              Organizational <span style={{ color: '#C7B381', fontWeight: 600 }}>Structure</span>
            </h2>
            <div style={{ width: isMobile ? '80px' : '120px', height: '4px', background: 'linear-gradient(90deg, transparent, #C7B381, transparent)', margin: '0 auto' }} />
          </div>

          {/* ── MOBILE: simple vertical stack ─────────────────────────── */}
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem' }}>

              {/* Tier 1 — Founder */}
              {companyData.founder.images.length > 0 && (
                <>
                  <CircularAvatar person={companyData.founder} size="180px" onClick={openProfile} cardKey="founder" isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  <ConnectorLine isMobile={isMobile} />
                </>
              )}

              {/* Tier 2 — Executive Director */}
              {companyData.executiveDirector && (
                <>
                  <CircularAvatar person={companyData.executiveDirector} size="160px" onClick={openProfile} cardKey="ed" isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  <ConnectorLine isMobile={isMobile} />
                </>
              )}

              {/* Tier 3 — Rabgay then Pema Lhamo (vertical on mobile) */}
              {companyData.rabgay && (
                <>
                  <CircularAvatar person={companyData.rabgay} size="155px" onClick={openProfile} cardKey="rabgay" isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  <ConnectorLine isMobile={isMobile} />
                </>
              )}
              {companyData.pemaLhamo && (
                <>
                  <CircularAvatar person={companyData.pemaLhamo} size="155px" onClick={openProfile} cardKey="pemalh" isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  {tier4.length > 0 && <ConnectorLine isMobile={isMobile} />}
                </>
              )}

              {/* Tier 4 — Sonam then Kinga (vertical on mobile) */}
              {tier4.map((gm, idx) => (
                <React.Fragment key={idx}>
                  <CircularAvatar person={gm} size="150px" onClick={openProfile} cardKey={`gm-${idx}`} isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  {idx < tier4.length - 1 && <ConnectorLine isMobile={isMobile} />}
                </React.Fragment>
              ))}
            </div>

          ) : (
            /* ── DESKTOP: full tree layout ──────────────────────────── */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3rem', position: 'relative' }}>

              {/* Tier 1 — Founder */}
              {companyData.founder.images.length > 0 && (
                <>
                  <CircularAvatar person={companyData.founder} size="220px" onClick={openProfile} cardKey="founder" isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  <ConnectorLine isMobile={isMobile} />
                </>
              )}

              {/* Tier 2 — Executive Director */}
              {companyData.executiveDirector && (
                <>
                  <CircularAvatar person={companyData.executiveDirector} size="190px" onClick={openProfile} cardKey="ed" isMobile={isMobile} hoveredCard={hoveredCard} setHoveredCard={setHoveredCard} />
                  {tier3.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '-1rem', marginBottom: '-1rem' }}>
                      <div style={{ width: '2px', height: '40px', background: 'linear-gradient(180deg, rgba(199,179,129,0.6), rgba(199,179,129,0.4))' }} />
                      {/* downward chevron */}
                      <svg width="32" height="20" viewBox="0 0 32 20" style={{ marginTop: '-2px' }}>
                        <defs>
                          <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" style={{ stopColor: 'rgba(199,179,129,0.4)', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: 'rgba(199,179,129,0.8)', stopOpacity: 1 }} />
                          </linearGradient>
                        </defs>
                        <path d="M16 20 L0 0 L8 0 L16 10 L24 0 L32 0 Z" fill="url(#arrowGradient)" stroke="#C7B381" strokeWidth="0.5" />
                      </svg>
                    </div>
                  )}
                </>
              )}

              {/* Tier 3 — Rabgay & Pema Lhamo side by side */}
              {tier3.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6rem',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  width: '100%',
                  maxWidth: '900px',
                  position: 'relative',
                  paddingTop: '2rem'
                }}>
                  {/* horizontal branch line */}
                  <svg style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', height: '100px', pointerEvents: 'none', zIndex: 0 }}>
                    <defs>
                      <linearGradient id="tier3Gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" style={{ stopColor: 'rgba(199,179,129,0.3)', stopOpacity: 1 }} />
                        <stop offset="50%" style={{ stopColor: 'rgba(199,179,129,0.6)', stopOpacity: 1 }} />
                        <stop offset="100%" style={{ stopColor: 'rgba(199,179,129,0.3)', stopOpacity: 1 }} />
                      </linearGradient>
                    </defs>
                    <line x1="25%" y1="0" x2="75%" y2="0" stroke="url(#tier3Gradient)" strokeWidth="2" />
                    <line x1="25%" y1="0" x2="25%" y2="40" stroke="rgba(199,179,129,0.5)" strokeWidth="2" />
                    <line x1="75%" y1="0" x2="75%" y2="40" stroke="rgba(199,179,129,0.5)" strokeWidth="2" />
                  </svg>

                  {tier3.map((person, idx) => (
                    <div key={idx} style={{ flex: 1, display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                      <CircularAvatar
                        person={person}
                        size="180px"
                        onClick={openProfile}
                        cardKey={idx === 0 ? 'rabgay' : 'pemalh'}
                        isMobile={isMobile}
                        hoveredCard={hoveredCard}
                        setHoveredCard={setHoveredCard}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Connector from Tier 3 to Tier 4 */}
              {tier3.length > 0 && tier4.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '-1rem', marginBottom: '-1rem' }}>
                  <div style={{ width: '2px', height: '40px', background: 'linear-gradient(180deg, rgba(199,179,129,0.6), rgba(199,179,129,0.4))' }} />
                  <svg width="32" height="20" viewBox="0 0 32 20" style={{ marginTop: '-2px' }}>
                    <path d="M16 20 L0 0 L8 0 L16 10 L24 0 L32 0 Z" fill="url(#arrowGradient)" stroke="#C7B381" strokeWidth="0.5" />
                  </svg>
                </div>
              )}

              {/* Tier 4 — Sonam & Kinga side by side */}
              {tier4.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6rem',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  width: '100%',
                  maxWidth: '900px',
                  position: 'relative',
                  paddingTop: '2rem'
                }}>
                  {tier4.length > 1 && <BranchConnector count={tier4.length} />}
                  {tier4.map((gm, idx) => (
                    <div key={idx} style={{ flex: 1, display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                      <CircularAvatar
                        person={gm}
                        size="170px"
                        onClick={openProfile}
                        cardKey={`gm-${idx}`}
                        isMobile={isMobile}
                        hoveredCard={hoveredCard}
                        setHoveredCard={setHoveredCard}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* FACILITIES */}
        {facilities.length > 0 && (
          <section style={{ marginBottom: isMobile ? '4rem' : 'clamp(6rem, 10vw, 10rem)' }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
              <h2 style={{ fontSize: isMobile ? '1.75rem' : 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 300 }}>
                Our <span style={{ color: '#C7B381', fontWeight: 600 }}>Facilities & Showrooms</span>
              </h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '1.5rem' : '2.5rem' }}>
              {facilities.map((facility, idx) => (
                <div
                  key={idx}
                  onMouseEnter={() => setHoveredCard(`facility-${idx}`)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    background: 'rgba(15,20,25,0.7)',
                    borderRadius: isMobile ? '14px' : '20px',
                    overflow: 'hidden',
                    border: '1px solid rgba(199,179,129,0.2)',
                    transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                    transform: hoveredCard === `facility-${idx}` ? 'translateY(-8px)' : 'translateY(0)',
                    boxShadow: hoveredCard === `facility-${idx}` ? '0 25px 60px rgba(0,0,0,0.6)' : '0 10px 30px rgba(0,0,0,0.4)'
                  }}
                >
                  <div style={{ position: 'relative', height: isMobile ? '250px' : '320px', overflow: 'hidden', background: '#000' }}>
                    {facility.gallery.map((img, i) => (
                      <img key={img} src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: facility.currentIndex === i ? 1 : 0, transition: 'opacity 1.5s ease-in-out', pointerEvents: 'none' }} />
                    ))}
                    {facility.gallery.length > 1 && (
                      <div style={{ position: 'absolute', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: isMobile ? '8px' : '10px', background: 'rgba(0,0,0,0.7)', padding: isMobile ? '6px 12px' : '8px 14px', borderRadius: '30px' }}>
                        {facility.gallery.map((_, i) => (
                          <div key={i} onClick={e => { e.stopPropagation(); facility.setIndex(i); }} style={{ width: facility.currentIndex === i ? (isMobile ? '24px' : '26px') : (isMobile ? '8px' : '10px'), height: isMobile ? '8px' : '10px', borderRadius: '999px', background: facility.currentIndex === i ? '#C7B381' : 'rgba(199,179,129,0.4)', transition: 'all 0.4s', cursor: 'pointer' }} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: isMobile ? '1.25rem' : '2rem' }}>
                    <h3 style={{ fontSize: isMobile ? '1.25rem' : '1.6rem', marginBottom: '0.75rem' }}>{facility.title}</h3>
                    <p style={{ color: '#ccc', lineHeight: 1.7, fontSize: isMobile ? '0.9rem' : '1rem' }}>{facility.description}</p>
                    {facility.staffImage && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.25rem', padding: isMobile ? '0.75rem' : '1rem', background: 'rgba(199,179,129,0.08)', borderRadius: '12px', border: '1px solid rgba(199,179,129,0.2)' }}>
                        <img src={facility.staffImage} alt="Staff" style={{ width: isMobile ? '60px' : '70px', height: isMobile ? '60px' : '70px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(199,179,129,0.3)' }} />
                        <div>
                          <div style={{ fontWeight: 500, color: '#C7B381', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>Staff In Charge</div>
                          <div style={{ color: '#aaa', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>Dedicated professionals</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CUSTOMERS */}
        {dbImages.customer.length > 0 && (
          <section style={{ marginBottom: isMobile ? '4rem' : 'clamp(6rem, 10vw, 10rem)' }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
              <h2 style={{ fontSize: isMobile ? '1.75rem' : 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 300 }}>
                Our Recent <span style={{ color: '#C7B381', fontWeight: 600 }}>Customers</span>
              </h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)', gap: isMobile ? '0.75rem' : '1.2rem' }}>
              {dbImages.customer.map((img, idx) => (
                <div key={img._id} style={{ aspectRatio: '1', borderRadius: isMobile ? '12px' : '16px', overflow: 'hidden', border: '1px solid rgba(199,179,129,0.2)', background: 'rgba(15,20,25,0.7)', transition: 'transform 0.3s ease', cursor: 'pointer' }}
                  onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1.05)')}
                  onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1)')}>
                  <img src={img.imageUrl} alt={img.alt || `Customer ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* VISION & MISSION */}
        <section style={{ marginBottom: isMobile ? '3rem' : 'clamp(4rem, 8vw, 6rem)' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '2rem' : '3rem' }}>
            <h2 style={{ fontSize: isMobile ? '1.75rem' : 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 300 }}>
              Vision & <span style={{ color: '#C7B381', fontWeight: 600 }}>Mission</span>
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '1.5rem' : '2.5rem', marginBottom: isMobile ? '2rem' : '4rem' }}>
            {[{ label: 'Vision', text: visionMission.vision }, { label: 'Mission', text: visionMission.mission }].map(({ label, text }) => (
              <div key={label} style={{ background: 'rgba(199,179,129,0.08)', padding: isMobile ? '1.5rem' : '2.5rem', borderRadius: isMobile ? '12px' : '16px', border: '1px solid rgba(199,179,129,0.2)' }}>
                <div style={{ color: '#C7B381', fontWeight: 600, marginBottom: '1rem', fontSize: isMobile ? '0.95rem' : '1.1rem' }}>{label}</div>
                <p style={{ lineHeight: 1.8, color: '#ccc', fontSize: isMobile ? '0.9rem' : '1rem' }}>{text}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '1rem' : '1.5rem' }}>
            {visionMission.values.map((v, i) => (
              <div key={i} style={{ background: 'rgba(15,20,25,0.7)', padding: isMobile ? '1.25rem' : '1.8rem', borderRadius: isMobile ? '10px' : '12px', textAlign: 'center', border: '1px solid rgba(199,179,129,0.2)', transition: 'transform 0.3s ease', cursor: 'default' }}
                onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(-5px)')}
                onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(0)')}>
                <h5 style={{ color: '#C7B381', fontWeight: 600, marginBottom: '0.5rem', fontSize: isMobile ? '0.95rem' : '1.1rem' }}>{v.title}</h5>
                <p style={{ color: '#aaa', fontSize: isMobile ? '0.8rem' : '0.95rem' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

      </div>

      <style>{`
        img { pointer-events: none; -webkit-user-drag: none; -webkit-touch-callout: none; }
        * { user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; }
        @media print { body { display: none; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default CompanyBackground;